import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from '@mui/material';
import JobDeliveryPhotos from './JobDeliveryPhotos';
import { googleMapsQueryUrl, formatJobDeliveryLine } from '../utils/jobMapsLink';

function formatPaymentStatusLabel(status) {
  if (!status) return 'Unknown';
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getPaymentStatusColor(status) {
  const colors = {
    paid: 'success',
    partial: 'warning',
    overdue: 'error',
    pending: 'info',
    not_submitted: 'default',
  };
  return colors[status] || 'default';
}

export default function CompletedJobDetailsDialog({
  open,
  job,
  onClose,
  isMobile = false,
  canAddPhotos = false,
  onJobUpdated = null,
}) {
  if (!job) return null;

  const address = formatJobDeliveryLine(job) || 'N/A';
  const completion = job.delivery_completion;
  const phone = job.customer_phone_number;
  const phoneHref = phone ? `tel:${String(phone).replace(/[^\d+]/g, '')}` : null;

  const handlePhotosAdded = (updatedJob) => {
    if (onJobUpdated) onJobUpdated(updatedJob);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      sx={{ '& .MuiDialog-paper': { m: { xs: 0, sm: 2 } } }}
    >
      <DialogTitle>
        Order Details {job.id ? `(${job.id.substring(0, 8)})` : ''}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {(job.customer_name || phone) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Customer
              </Typography>
              {job.customer_name && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Company:</strong> {job.customer_name}
                </Typography>
              )}
              {phone && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Phone:</strong>{' '}
                  <Link href={phoneHref} underline="hover">
                    {phone}
                  </Link>
                </Typography>
              )}
            </Box>
          )}

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Delivery information
          </Typography>
          {job.job_status && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Status:</strong>{' '}
              {job.job_status.charAt(0).toUpperCase() + job.job_status.slice(1).replace('_', ' ')}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Location:</strong>{' '}
            {address !== 'N/A' ? (
              <Link
                href={googleMapsQueryUrl(address)}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                {address}
              </Link>
            ) : (
              'N/A'
            )}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5, whiteSpace: 'pre-wrap' }}>
            <strong>Delivery notes:</strong>{' '}
            {job.delivery_notes?.trim() ? job.delivery_notes : '—'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Requested:</strong>{' '}
            {job.diesel_quantity > 0 && `${job.diesel_quantity} gal Diesel`}
            {job.diesel_quantity > 0 && job.def_quantity > 0 && ' / '}
            {job.def_quantity > 0 && `${job.def_quantity} gal DEF`}
            {!job.diesel_quantity && !job.def_quantity && 'N/A'}
          </Typography>
          {(job.delivered_diesel > 0 || job.delivered_def > 0) && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Delivered:</strong>{' '}
              {job.delivered_diesel > 0 && `${job.delivered_diesel} gal Diesel`}
              {job.delivered_diesel > 0 && job.delivered_def > 0 && ' / '}
              {job.delivered_def > 0 && `${job.delivered_def} gal DEF`}
            </Typography>
          )}
          {job.driver_name && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Driver:</strong> {job.driver_name}
            </Typography>
          )}
          {job.completed_at && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Completed:</strong> {new Date(job.completed_at).toLocaleString()}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Payment status:</strong>{' '}
            <Chip
              label={formatPaymentStatusLabel(job.payment_status)}
              color={getPaymentStatusColor(job.payment_status)}
              size="small"
              sx={{ verticalAlign: 'middle' }}
            />
          </Typography>
          {job.invoice_number && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Invoice:</strong> {job.invoice_number}
            </Typography>
          )}

          {completion && job.job_status === 'complete' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Completion record
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Diesel delivered:</strong> {completion.diesel_delivered || 0} gal
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>DEF delivered:</strong> {completion.def_delivered || 0} gal
              </Typography>
              {completion.completion_notes && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Driver notes:</strong> {completion.completion_notes}
                </Typography>
              )}
            </Box>
          )}

          {job.job_status === 'complete' && (
            <JobDeliveryPhotos
              photos={completion?.photos}
              jobId={job.id}
              canAdd={canAddPhotos}
              onPhotosAdded={handlePhotosAdded}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
